import axios from 'axios'
import type { AddonParamData, AddonProduct, AddonProductEnum, BaseAddressDto, FindAddressResult, InsurerWithPaymentFrequencies, Item, KadastralAreaInfo, SimpleItem } from 'BrokerTsLib/src/dtos'
import type { Insurer, Risk, SelectionType } from 'brokertslib/src/enums'
import { Product } from 'brokertslib/src/enums'
import { longStaleTime } from 'brokertslib/src/hooks'
import { returnArray } from 'brokertslib/src/Utils/reactQueryUtils'
import { useCallback } from 'react'
import { useMutation, useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'

import { useAuth } from '../context/AuthContext'
import { useApp } from './useApp'

export const baseUrl = import.meta.env.VITE_BASE_URL
const apiUrl = import.meta.env.VITE_ENUM_API
const apiTravelUrl = import.meta.env.VITE_ENUM_TRAVEL_API
const apiPropertyUrl = import.meta.env.VITE_ENUM_PROPERTY_API
const apiLiabilityUrl = import.meta.env.VITE_ENUM_LIABILITY_API
const addonEnumApiUrl = import.meta.env.VITE_ENUM_ADDON_API

export const useUrl = (productOverride?: Product) => {
  const { product } = useApp()

  switch (product ?? productOverride) {
    case Product.TravelInsurance:
      return `${baseUrl}/${apiTravelUrl}`
    case Product.PropertyInsurance:
      return `${baseUrl}/${apiPropertyUrl}`
    case Product.LiabilityInsurance:
      return `${baseUrl}/${apiLiabilityUrl}`
    default:
      return `${baseUrl}/${apiUrl}`
  }
}

export type GenericQueryOptions<T> = Omit<UseQueryOptions<T, any, T, any>, 'queryKey' | 'queryFn'> | undefined

export const useSelections = (selectionType?: SelectionType, options?: GenericQueryOptions<Item[]>): UseQueryResult<Item[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['selection', selectionType, product],
    queryFn: () => axios.get<Item[]>(`${url}/GetSelections?type=${selectionType}`).then(returnArray),
    staleTime: longStaleTime,
    retry: 3,
    retryDelay: attemptIndex => 2000 * 2 ** attemptIndex,
    ...options
  })
}

export const useInsurers = (
  addonOnly: boolean | null,
  includeInvalid: boolean | null,
  options?: GenericQueryOptions<Item[]>,
  productOverride?: Product
) => {
  const url = useUrl(productOverride)

  const response = useQuery({
    queryKey: ['insurer', addonOnly, includeInvalid, url],
    queryFn: () => axios.get<Item[]>(`${url}/GetInsurers?addonProviders=${addonOnly}&includeInvalid=${includeInvalid}`).then(returnArray),
    staleTime: longStaleTime,
    ...options
  })

  const isInsurersAvailable = useCallback(
    (insurers: Insurer[]) => (response.data?.filter((x) => insurers.includes(x.id))?.length ?? 0) > 0,
    [response.data],
  )

  const isInsurerAvailable = useCallback(
    (insurer: Insurer) => isInsurersAvailable([insurer]),
    [isInsurersAvailable],
  )

  return {
    ...response,
    isInsurersAvailable,
    isInsurerAvailable
  }
}

export const useInsurerPaymentFrequencies = (options?: GenericQueryOptions<InsurerWithPaymentFrequencies[]>): UseQueryResult<InsurerWithPaymentFrequencies[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['InsurerWithPaymentFrequencies', url, product],
    queryFn: () => axios.get<InsurerWithPaymentFrequencies[]>(`${url}/GetInsurerWithAllowedPaymentFrequencies`).then(returnArray),
    staleTime: longStaleTime,
    ...options
  })
}

export const useCountries = (enabled?: boolean, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<SimpleItem[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['country', product],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetCountries`).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, enabled, ...options })
  })
}

export const useRegions = (postCode: string, options?: GenericQueryOptions<BaseAddressDto[]>): UseQueryResult<BaseAddressDto[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['region', postCode, product],
    queryFn: () => axios.get<BaseAddressDto[]>(`${url}/GetRegions?postCode=${postCode}`).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

export const useGetRiskVariants = (risk?: Risk, options?: GenericQueryOptions<Item[]>): UseQueryResult<Item[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['riskVariant', risk, product],
    queryFn: () => axios.get<Item[]>(`${url}/GetRiskVariants?riskId=${risk}`).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

export const useYearKm = (categoryId: number, insurerId?: number, options?: GenericQueryOptions<SimpleItem[]>): UseQueryResult<SimpleItem[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useYearKm', categoryId, product],
    queryFn: () => axios.get<SimpleItem[]>(`${url}/GetYearKm`, { params: { categoryId, insurerId } }).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

export const useGetAddonProductEnums = () =>
  useMutation({
    mutationFn: (data: { offerId: number, riskVariantId: number, changedParams: AddonParamData[], filterOutput: boolean }) =>
      axios.post<AddonProductEnum[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonProductEnums`, data)
  })

export const useGetAddonRiskVariantProducts = () =>
  useMutation({
    mutationFn: (data: { offerId: number, riskVariantId: number }) =>
      axios.post<SimpleItem[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonRiskVariantProducts?offerId=${data.offerId}&riskVariantId=${data.riskVariantId}`)
  })

export const useGetAddonParamValues = () =>
  useMutation({
    mutationFn: (data: { mainOfferId: number, addonOfferId: number, paramName: string }) =>
      axios.get<AddonProduct[]>(`${baseUrl}/${addonEnumApiUrl}/GetAddonParamValues?mainOfferId=${data.mainOfferId}&addonOfferId=${data.addonOfferId}&paramName=${data.paramName}`)
  })

export const useFindStreet = (search: string, options?: GenericQueryOptions<string[]>): UseQueryResult<string[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useFindStreet', search, product],
    queryFn: () => axios.get<string[]>(`${url}/FindStreet?search=${search}`).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

export const useFindAddress = (street?: string, searchQuery?: string, options?: GenericQueryOptions<FindAddressResult[]>): UseQueryResult<FindAddressResult[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useFindAddress', street, searchQuery, product],
    queryFn: () => axios.get<FindAddressResult[]>(`${url}/FindAddress`, { params: { street, searchQuery } }).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

export const useFindKatUzemi = (zipCode: string, options?: GenericQueryOptions<KadastralAreaInfo[]>): UseQueryResult<KadastralAreaInfo[], any> => {
  const url = useUrl()
  const { product } = useApp()

  return useQuery({
    queryKey: ['useFindKatUzemi', zipCode, product],
    queryFn: () => axios.get<KadastralAreaInfo[]>(`${url}/FindKatUzemi?psc=${zipCode}`).then(returnArray),
    ...RequiredAuthOpts({ staleTime: longStaleTime, ...options })
  })
}

const RequiredAuthOpts = <T>(opt: GenericQueryOptions<T>): GenericQueryOptions<T> => {
  const ctx = useAuth()

  return { ...opt, enabled: opt?.enabled && ctx?.user?.logged }
}